
import React from 'react';
import type { QuizConfig } from '../types';

interface QuizHeaderProps {
  currentStep: number;
  totalSteps: number;
  config: QuizConfig;
  onBack?: () => void;
  coins?: number;
  coinContext?: string;
  isCanvas?: boolean;
}

const QuizHeader: React.FC<QuizHeaderProps> = ({ currentStep, totalSteps, config, onBack, coins, coinContext, isCanvas = false }) => {
  const headerConfig = config.header || {};
  const showBackButton = headerConfig.showBackButton !== false; // Default true
  const showStepIndicator = headerConfig.showStepIndicator !== false; // Default true
  const coverMode = headerConfig.hideOnFirstPage === true;
  const onCover = coverMode && currentStep === 0;
  const logoUrl = headerConfig.logoUrl;
  const progressBarStyle = headerConfig.progressBarStyle || 'default';
  const effectiveTotal = coverMode ? Math.max(0, totalSteps - 1) : totalSteps;
  const effectiveCurrent = coverMode ? Math.max(0, currentStep - 1) : currentStep;
  const progressPercentage = effectiveTotal > 0 ? Math.max(0, Math.min(100, ((effectiveCurrent + 1) / effectiveTotal) * 100)) : 0;
  const shouldShowBackButton = showBackButton && currentStep > 0 && !!onBack;
  const coinsEnabled = !onCover && (config.gamification?.coinsEnabled === true);
  const coinsPreText = (config.gamification?.coinsPreText ?? 'Seu Saldo: ').trim();
  const coinsPostText = (config.gamification?.coinsPostText ?? 'ptos').trim();
  const [pulse, setPulse] = React.useState(false);
  const prevCoinsRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (!coinsEnabled) return;
    const prev = prevCoinsRef.current;
    prevCoinsRef.current = coins;
    if (typeof prev === 'number' && typeof coins === 'number' && coins > prev) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 400);
      return () => clearTimeout(t);
    }
  }, [coinsEnabled, coins]);

  const widthClass = (() => {
    switch (config.contentWidth) {
      case 'small': return 'max-w-lg';
      case 'medium': return 'max-w-2xl';
      case 'large': return 'max-w-4xl';
      default: return 'max-w-lg';
    }
  })();

  const isBoxed = config.containerStyle === 'boxed';

  const borderRadius = (() => {
    switch (config.borderRadius) {
      case 'heavy': return '1.5rem';
      case 'medium': return '1rem';
      case 'light': return '0.5rem';
      default: return '0';
    }
  })();

  return (
    <div className={`w-full ${isCanvas ? 'px-4' : ''}`} style={{ backgroundColor: isBoxed ? 'transparent' : config.colors.background }}>
      <div
        className={`w-full ${widthClass} mx-auto ${isBoxed ? 'shadow-sm mt-4 mb-1' : ''} pt-0 md:pt-[10px]`}
        style={{
          backgroundColor: isBoxed ? config.colors.background : 'transparent',
          borderRadius: isBoxed ? borderRadius : '0'
        }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          {/* Botão Voltar e Logo - Esquerda */}
          <div className={`flex items-center gap-3 ${config.header?.logoPosition === 'left' ? 'flex-0' : 'flex-1 justify-start'}`}>
            {shouldShowBackButton && (
              <button
                onClick={onBack}
                className="p-0.5 transition-opacity hover:opacity-70 flex-shrink-0"
                aria-label="Voltar"
                disabled={!onBack}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 text-black"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}
            {config.header?.logoPosition === 'left' && logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                className={`flex-shrink-0 ${config.header?.logoSize === 'large' ? 'max-w-[220px] max-h-[220px] object-contain' : 'max-w-[128px] max-h-[128px] object-contain'}`}
              />
            )}
          </div>

          {/* Logo - Center Position */}
          {logoUrl && config.header?.logoPosition !== 'left' && (
            <div className="flex-1 flex justify-center">
              <img
                src={logoUrl}
                alt="Logo"
                className={config.header?.logoSize === 'large' ? 'max-w-[220px] max-h-[220px] object-contain' : 'max-w-[128px] max-h-[128px] object-contain'}
              />
            </div>
          )}

          <div className="flex-1 flex justify-end">
            {coinsEnabled ? (
              config.gamification?.style === 'money' ? (
                <div
                  data-coin-target="true"
                  data-coin-context={coinContext || ''}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-dashed transition-transform ${pulse ? 'scale-110' : 'scale-100'}`}
                  style={{ borderColor: '#86efac', backgroundColor: '#f7fef9', fontFamily: config.fontFamily }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600" viewBox="0 0 640 640" fill="currentColor">
                    <path d="M128 96C92.7 96 64 124.7 64 160L64 448C64 483.3 92.7 512 128 512L512 512C547.3 512 576 483.3 576 448L576 256C576 220.7 547.3 192 512 192L136 192C122.7 192 112 181.3 112 168C112 154.7 122.7 144 136 144L520 144C533.3 144 544 133.3 544 120C544 106.7 533.3 96 520 96L128 96zM480 320C497.7 320 512 334.3 512 352C512 369.7 497.7 384 480 384C462.3 384 448 369.7 448 352C448 334.3 462.3 320 480 320z" />
                  </svg>
                  <div className={`flex items-center font-bold leading-none ${config.fontSizes.option} whitespace-nowrap`}>
                    {coinsPreText && <span className="mr-1 text-green-600">{coinsPreText}</span>}
                    <span className="text-green-600">{typeof coins === 'number' ? coins : 0}</span>
                    {coinsPostText && <span className="ml-1 text-green-600">{coinsPostText}</span>}
                  </div>
                </div>
              ) : (
                <div data-coin-target="true" data-coin-context={coinContext || ''} className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-transform ${pulse ? 'scale-110' : 'scale-100'}`} style={{ borderColor: '#f59e0b', backgroundColor: '#fef3c7', fontFamily: config.fontFamily }}>
                  {coinsPreText && <span className="text-xs" style={{ color: '#92400e' }}>{coinsPreText}</span>}
                  <span style={{ fontSize: '1rem' }}>🪙</span>
                  <span className="text-sm font-bold" style={{ color: '#b45309' }}>{typeof coins === 'number' ? coins : 0}</span>
                  {coinsPostText && <span className="text-xs" style={{ color: '#92400e' }}>{coinsPostText}</span>}
                </div>
              )
            ) : (
              showStepIndicator && !onCover && (
                <span
                  className="text-sm font-medium"
                  style={{ color: config.colors.primary }}
                >
                  {effectiveCurrent + 1} / {effectiveTotal}
                </span>
              )
            )}
          </div>
        </div>

        {/* Barra de Progresso */}
        {progressBarStyle !== 'hidden' && !onCover && (
          <div className="px-4 pb-3">
            <div className={`w-full bg-slate-200 ${progressBarStyle === 'flat' ? 'h-1' : 'h-2 rounded-full'}`}>
              <div
                className={`${progressBarStyle === 'flat' ? 'h-1' : 'h-2 rounded-full'} transition-all duration-500 ease-out`}
                style={{ width: `${progressPercentage}%`, backgroundColor: config.colors.primary }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizHeader;

